import React, {useCallback, useEffect, useRef, useState} from "react";
import {C} from "../styles";
import SECTION_FIELDS from "../landing/sectionFields.json";
import {moveSection, moveSectionBy, setVisible, setBackground, backgroundIdOf, featureSection, updateSection, sanitizePage, addCustomSection, removeSection} from "../landing/pageModel";
import {BACKGROUNDS} from "../landing/backgrounds";
import {apiAdminGetLanding, apiAdminSaveLandingDraft} from "../api/landing";
import LandingSectionEditor from "./LandingSectionEditor";
import LandingCustomEditor from "./LandingCustomEditor";
import {useConfirm} from "./FeedbackProvider";
import BLOCK_FIELDS from "../landing/blockFields.json";
import LandingPublishBar from "./LandingPublishBar";
import LandingReviewPanel from "./LandingReviewPanel";

const PREVIEW_WIDTHS = {desktop: "100%", phone: 390};

const iconButton = {
    border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, width: 32, height: 32, cursor: "pointer", fontSize: 15,
};

// "2026-12-01T09:00" (champ datetime-local, heure locale) <-> ISO 8601 avec fuseau (ce que valide le serveur).
const toIso = (local) => (local ? new Date(local).toISOString() : null);
function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function errorMessage(result) {
    if (Array.isArray(result.errors) && result.errors.length > 0) {
        return result.errors.map(e => `${e.path} : ${e.message}`).join(" — ");
    }
    return result.message || result.error;
}

// Nom affiché dans la liste : celui que l'admin a donné à une rubrique libre, sinon le nom du type.
const labelOf = (section) => (section.type === "custom"
    ? ((section.settings && section.settings.name) || SECTION_FIELDS.custom.label)
    : SECTION_FIELDS[section.type].label);

export default function AdminLanding({user, saveDelayMs = 400}) {
    const confirm = useConfirm();
    const [state, setState] = useState({loading: true});
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [dragId, setDragId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [previewKey, setPreviewKey] = useState(0);
    const [previewMode, setPreviewMode] = useState("desktop");
    const timer = useRef(null);
    const latest = useRef({});
    // Révision du brouillon telle que ce poste la connaît (null = aucun brouillon) ; conflict = un autre admin a modifié
    // le brouillon : on n'enregistre plus rien tant que l'admin n'a pas rechargé.
    const revision = useRef(null);
    const [conflict, setConflict] = useState(false);
    const conflicted = useRef(false);

    // Enregistrements l'un après l'autre : une requête plus ancienne ne peut pas arriver après une plus récente.
    const inFlight = useRef(null);

    const save = useCallback(async () => {
        const pending = latest.current;
        if (!pending.page) return true;
        if (conflicted.current) return false;
        const result = await apiAdminSaveLandingDraft(pending.page, pending.basedOn, revision.current);
        if (result && result.error === "DRAFT_CONFLICT") {
            conflicted.current = true;
            setConflict(true);
            setError(errorMessage(result));
            return false;
        }
        if (result && result.error) {
            // Le changement reste « en attente » : un prochain enregistrement ou la publication le réessaie.
            setError(errorMessage(result));
            return false;
        }
        if (typeof result.revision === "number") revision.current = result.revision;
        // Un changement arrivé pendant l'envoi reste en attente ; sinon tout est enregistré.
        if (latest.current === pending) latest.current = {};
        setError("");
        setPreviewKey(k => k + 1);
        return true;
    }, []);

    const persist = useCallback(() => {
        const previous = inFlight.current;
        const run = previous ? previous.then(save) : save();
        const tracked = run.catch(() => {}).then(() => { if (inFlight.current === tracked) inFlight.current = null; });
        inFlight.current = tracked;
        return run;
    }, [save]);

    const load = useCallback(async () => {
        const result = await apiAdminGetLanding();
        if (!result || result.error) {
            setState({loading: false, failed: true});
            setError(result ? errorMessage(result) : "Impossible de charger la landing.");
            return;
        }
        setError("");
        revision.current = typeof result.draft_revision === "number" ? result.draft_revision : null;
        conflicted.current = false;
        setConflict(false);
        const {page, removed} = sanitizePage(result.page);
        setNotice(removed === 0 ? "" : removed === 1
            ? "1 élément obsolète a été retiré de cette page (il n'existe plus dans l'éditeur) ; il disparaîtra à votre prochain enregistrement."
            : `${removed} éléments obsolètes ont été retirés de cette page (ils n'existent plus dans l'éditeur) ; ils disparaîtront à votre prochain enregistrement.`);
        setState({loading: false, page, isDraft: result.is_draft, liveVersionId: result.live_version_id, basedOn: result.based_on_version_id, aiAvailable: Boolean(result.ai_available)});
    }, []);

    useEffect(() => { load(); }, [load]);
    // Quitter l'onglet avec un changement en attente : on l'enregistre plutôt que de le perdre.
    useEffect(() => () => { clearTimeout(timer.current); persist(); }, [persist]);

    // Chaque changement met l'état à jour tout de suite, puis enregistre le brouillon (regroupé) et recharge l'aperçu.
    function change(nextPage) {
        const basedOn = state.isDraft ? state.basedOn : state.liveVersionId;
        setState(s => ({...s, page: nextPage, isDraft: true, basedOn}));
        latest.current = {page: nextPage, basedOn};
        clearTimeout(timer.current);
        timer.current = setTimeout(persist, saveDelayMs);
    }

    // Enregistre tout de suite le dernier changement en attente (avant une publication) ; false si l'enregistrement échoue.
    async function flush() {
        clearTimeout(timer.current);
        return persist();
    }

    // Conflit d'édition : on abandonne les changements locaux non enregistrés et on repart du brouillon de l'autre admin.
    async function reloadAfterConflict() {
        clearTimeout(timer.current);
        latest.current = {};
        await reload();
    }

    // Après publication ou abandon : on repart de l'état du serveur et on recharge l'aperçu.
    async function reload() {
        await load();
        setPreviewKey(k => k + 1);
    }

    function changeDate(section, field, value) {
        const patch = {[field]: toIso(value)};
        const next = {...section, ...patch};
        if (next.starts_at && next.ends_at && Date.parse(next.ends_at) <= Date.parse(next.starts_at)) {
            setError("La fin doit être postérieure au début.");
            return;
        }
        change(updateSection(state.page, section.id, patch));
    }

    function createCustomSection() {
        const {page, id} = addCustomSection(state.page);
        if (!id) return;
        change(page);
        setEditingId(id);
    }

    async function deleteCustomSection(section) {
        if (!(await confirm(`Supprimer la rubrique « ${labelOf(section)} » et tous ses blocs ?`, {danger: true}))) return;
        change(removeSection(state.page, section.id));
    }

    if (state.loading) return <p>Chargement…</p>;
    if (state.failed) return <p role="alert" style={{color: C.danger}}>{error}</p>;

    const sections = state.page.sections.filter(s => SECTION_FIELDS[s.type]);

    const editing = editingId ? state.page.sections.find(s => s.id === editingId) : null;
    const customCount = state.page.sections.filter(s => s.type === "custom").length;

    return (
        <div>
            {editing && editing.type === "custom" && (
                <LandingCustomEditor
                    section={editing}
                    aiAvailable={state.aiAvailable}
                    onClose={() => setEditingId(null)}
                    onSave={(saved) => { change(updateSection(state.page, editing.id, {settings: saved.settings, blocks: saved.blocks})); setEditingId(null); }}
                />
            )}
            {editing && editing.type !== "custom" && (
                <LandingSectionEditor
                    section={editing}
                    aiAvailable={state.aiAvailable}
                    onClose={() => setEditingId(null)}
                    onSave={(data) => { change(updateSection(state.page, editing.id, {data})); setEditingId(null); }}
                />
            )}
            <div style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap"}}>
                <h3 style={{margin: 0}}>Landing page</h3>
                {state.isDraft && (
                    <span style={{background: "#FFF3CD", color: "#664D03", borderRadius: 12, padding: "3px 10px", fontSize: 12, fontWeight: 600}}>
                        Brouillon non publié
                    </span>
                )}
                <button type="button" onClick={createCustomSection} disabled={customCount >= BLOCK_FIELDS.maxCustomSections}
                        style={{marginLeft: "auto", border: `1px solid ${C.primary}`, background: "#fff", color: C.primary, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontWeight: 600}}>
                    + Nouvelle rubrique
                </button>
            </div>
            {error && <p role="alert" style={{color: C.danger, margin: "0 0 12px"}}>{error}</p>}
            {conflict && (
                <button type="button" onClick={reloadAfterConflict} style={{border: `1px solid ${C.primary}`, background: "#fff", color: C.primary, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontWeight: 600, margin: "0 0 12px"}}>
                    Recharger le brouillon (mes derniers changements seront perdus)
                </button>
            )}
            {notice && <p style={{background: "#FFF3CD", color: "#664D03", borderRadius: 6, padding: "6px 10px", margin: "0 0 12px", fontSize: 13}}>{notice}</p>}

            <div style={{display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap"}}>
                <div style={{flex: "1 1 360px", minWidth: 300}}>
                    {sections.map((s, i) => {
                        const label = labelOf(s);
                        const isCustom = s.type === "custom";
                        return (
                            <div
                                key={s.id}
                                data-testid="landing-row"
                                draggable
                                onDragStart={e => {
                                    // Firefox n'amorce pas le glisser-déposer sans donnée dans dataTransfer.
                                    if (e.dataTransfer) {
                                        e.dataTransfer.effectAllowed = "move";
                                        e.dataTransfer.setData("text/plain", s.id);
                                    }
                                    setDragId(s.id);
                                }}
                                onDragOver={e => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; }}
                                onDrop={e => {
                                    e.preventDefault();
                                    if (dragId && dragId !== s.id) change(moveSection(state.page, dragId, state.page.sections.findIndex(x => x.id === s.id)));
                                    setDragId(null);
                                }}
                                style={{
                                    border: `1px solid ${s.featured ? "#F2C14E" : C.border}`, background: s.featured ? "#FFF8E1" : "#fff",
                                    opacity: s.visible ? 1 : 0.6, borderRadius: 8, padding: "8px 10px", marginBottom: 8,
                                }}
                            >
                                <div style={{display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
                                    <span aria-hidden="true" style={{cursor: "grab", fontSize: 18}}>⠿</span>
                                    <strong data-testid="landing-row-label" style={{flex: 1}}>{label}</strong>
                                    {s.featured && <span style={{fontSize: 12}}>À la une</span>}
                                    {!s.visible && <span style={{fontSize: 12}}>Masquée</span>}
                                    <button type="button" style={iconButton} aria-label={`Monter ${label}`} disabled={i === 0}
                                            onClick={() => change(moveSectionBy(state.page, s.id, -1))}>↑</button>
                                    <button type="button" style={iconButton} aria-label={`Descendre ${label}`} disabled={i === sections.length - 1}
                                            onClick={() => change(moveSectionBy(state.page, s.id, 1))}>↓</button>
                                    <button type="button" style={iconButton} aria-label={`${s.visible ? "Masquer" : "Afficher"} ${label}`}
                                            onClick={() => change(setVisible(state.page, s.id, !s.visible))}>{s.visible ? "👁" : "🚫"}</button>
                                    <button type="button" style={iconButton} aria-label={`Mettre ${label} à la une`} disabled={s.type === "hero"}
                                            onClick={() => change(featureSection(state.page, s.id))}>⭐</button>
                                    <button type="button" style={iconButton}
                                            aria-label={isCustom ? `Modifier la rubrique ${label}` : `Modifier les textes de ${label}`}
                                            onClick={() => setEditingId(s.id)}>✏️</button>
                                    {isCustom && (
                                        <button type="button" style={iconButton} aria-label={`Supprimer la rubrique ${label}`}
                                                onClick={() => deleteCustomSection(s)}>🗑</button>
                                    )}
                                </div>
                                <div style={{display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", fontSize: 12}}>
                                    <label>
                                        <span style={{display: "block"}}>À partir de</span>
                                        <input type="datetime-local" aria-label={`Afficher ${label} à partir de`}
                                               value={toLocalInput(s.starts_at)} onChange={e => changeDate(s, "starts_at", e.target.value)}/>
                                    </label>
                                    <label>
                                        <span style={{display: "block"}}>Jusqu'à</span>
                                        <input type="datetime-local" aria-label={`Afficher ${label} jusqu'à`}
                                               value={toLocalInput(s.ends_at)} onChange={e => changeDate(s, "ends_at", e.target.value)}/>
                                    </label>
                                    <label>
                                        <span style={{display: "block"}}>Fond</span>
                                        <select aria-label={`Fond de ${label}`} value={backgroundIdOf(s)}
                                                onChange={e => change(setBackground(state.page, s.id, e.target.value))}>
                                            {!isCustom && <option value="">Par défaut (d'origine)</option>}
                                            {BACKGROUNDS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                                        </select>
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{flex: "1 1 360px", minWidth: 300}}>
                    <div style={{display: "flex", gap: 6, marginBottom: 8}}>
                        {Object.keys(PREVIEW_WIDTHS).map(mode => (
                            <button key={mode} type="button" onClick={() => setPreviewMode(mode)} aria-pressed={previewMode === mode}
                                    style={{border: `1px solid ${previewMode === mode ? C.primary : C.border}`, background: previewMode === mode ? C.primaryLight : "#fff", borderRadius: 16, padding: "4px 12px", cursor: "pointer"}}>
                                {mode === "desktop" ? "Ordinateur" : "Téléphone"}
                            </button>
                        ))}
                    </div>
                    <iframe
                        key={previewKey}
                        title="Aperçu de la landing"
                        src="/landing-preview"
                        style={{width: PREVIEW_WIDTHS[previewMode], maxWidth: "100%", height: 640, border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff"}}
                    />
                </div>
            </div>

            <LandingReviewPanel
                page={state.page}
                aiAvailable={state.aiAvailable}
                beforeReview={flush}
                onOpenSection={(sectionId) => setEditingId(sectionId)}
            />

            <LandingPublishBar
                isDraft={state.isDraft}
                liveVersionId={state.liveVersionId}
                beforePublish={flush}
                onPublished={reload}
                onDiscarded={reload}
            />
        </div>
    );
}
